// ── resources — the tools, clients and reading that go well with a memory ───
// A curated grid, not a directory: each entry is here because someone with an
// innernet is likely to reach for it. Every url resolves for a stranger (checked
// the day it was added). Marks come from BrandIcon — real logos, never emoji.

import type { Resource } from "./types";

export const RESOURCES: Resource[] = [
  // ── ai tools innernet connects to ────────────────────────────────────────
  { key: "claude-code", name: "claude code", brand: "claude", kind: "ai tools", connects: true,
    url: "https://docs.anthropic.com/en/docs/claude-code/overview",
    description: "anthropic's terminal agent. `npx innernet` wires it to your memory in one line." },
  { key: "claude-desktop", name: "claude desktop", brand: "claude", kind: "ai tools", connects: true,
    url: "https://claude.ai/download",
    description: "claude on your machine, with connectors. add innernet as a remote mcp server and it reads what you keep." },
  { key: "chatgpt", name: "chatgpt", brand: "openai", kind: "ai tools", connects: true,
    url: "https://chatgpt.com",
    description: "developer mode takes a custom mcp connector; innernet is one, so chatgpt can read the same memory claude does." },
  { key: "codex", name: "codex cli", brand: "codex", kind: "ai tools", connects: true,
    url: "https://github.com/openai/codex",
    description: "openai's coding agent for the terminal. speaks mcp; connects like everything else." },
  { key: "gemini-cli", name: "gemini cli", brand: "gemini", kind: "ai tools", connects: true,
    url: "https://github.com/google-gemini/gemini-cli",
    description: "google's terminal agent, open source, mcp-capable." },
  // ── editors that speak mcp ───────────────────────────────────────────────
  { key: "cursor", name: "cursor", brand: "cursor", kind: "editors", connects: true,
    url: "https://cursor.com",
    description: "the ai editor most people mean. add innernet under mcp servers and every chat starts already knowing the project." },
  { key: "windsurf", name: "windsurf", brand: "windsurf", kind: "editors", connects: true,
    url: "https://windsurf.com",
    description: "an agentic editor with an mcp panel of its own." },
  { key: "cline", name: "cline", brand: "cline", kind: "editors", connects: true,
    url: "https://cline.bot",
    description: "an open-source coding agent inside vs code; mcp servers plug straight in." },
  { key: "continue", name: "continue", brand: "continue", kind: "editors", connects: true,
    url: "https://continue.dev",
    description: "open-source ai for vs code and jetbrains, with mcp support." },
  { key: "zed", name: "zed", brand: "zed", kind: "editors", connects: true,
    url: "https://zed.dev",
    description: "a fast editor with an agent panel; context servers are how it says mcp." },
  // ── mcp itself ───────────────────────────────────────────────────────────
  { key: "mcp-spec", name: "model context protocol", kind: "mcp",
    url: "https://modelcontextprotocol.io",
    description: "the open protocol every one of these tools speaks. innernet is one server on it; the door to your memory." },
  { key: "mcp-inspector", name: "mcp inspector", brand: "github", kind: "mcp",
    url: "https://github.com/modelcontextprotocol/inspector",
    description: "a visual client for poking at any mcp server, the fastest way to see what a tool actually returns." },
  { key: "mcp-registry", name: "mcp registry", kind: "mcp",
    url: "https://registry.modelcontextprotocol.io",
    description: "the official, open catalogue of mcp servers. innernet is listed there as `live.innernet/innernet`." },
  { key: "innernet-mcp", name: "innernet's mcp server", brand: "innernet", kind: "mcp", connects: true,
    url: "/docs",
    description: "innernet.live/api/mcp: thirty-odd tools, one memory. the docs explain each and when a model reaches for it." },
  // ── skills, elsewhere ────────────────────────────────────────────────────
  { key: "anthropic-skills", name: "anthropic · skills", brand: "github", kind: "skills",
    url: "https://github.com/anthropics/skills",
    description: "the reference collection: documents, design, mcp building, and the skill-creator that teaches the format." },
  { key: "superpowers", name: "superpowers", brand: "github", kind: "skills",
    url: "https://github.com/obra/superpowers",
    description: "a community library of working-method skills: brainstorming, tdd, systematic debugging, and more." },
  { key: "vercel-agent-skills", name: "vercel · agent skills", brand: "github", kind: "skills",
    url: "https://github.com/vercel-labs/agent-skills",
    description: "vercel's own skills for react, next.js and web interface review." },
  { key: "skills-sh", name: "skills.sh", brand: "web", kind: "skills",
    url: "https://skills.sh",
    description: "a directory of agent skills across the ecosystem, searchable." },
  { key: "innernet-skill-format", name: "the skill format", brand: "innernet", kind: "skills",
    url: "/skills/skill-format",
    description: "how innernet writes a skill: one markdown file, a manifest on top, a link that is the interface." },
  // ── reading ──────────────────────────────────────────────────────────────
  { key: "innernet-docs", name: "innernet docs", brand: "innernet", kind: "reading",
    url: "/docs",
    description: "what innernet is, how the memory is shaped, and every tool on the door." },
  { key: "innernet-company", name: "letters from innernet", brand: "innernet", kind: "reading",
    url: "/company",
    description: "release notes and letters: what changed, and why we thought it should." },
  { key: "mcp-docs-clients", name: "mcp, the clients", kind: "reading",
    url: "https://modelcontextprotocol.io/clients",
    description: "the maintained list of every client that speaks mcp, and which parts of the protocol each supports." },
];

/** resource kinds in the order the chips read, keeping only kinds that have members. */
export function resourceKinds(list: Resource[] = RESOURCES): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of list) if (!seen.has(r.kind)) { seen.add(r.kind); out.push(r.kind); }
  return out;
}
